// components/FieldRenderer.js
const FieldRenderer = {
  renderField: (
    field,
    offsetWidth,
    offsetHeight,
    index,
    handleFieldClick,
    isAdmin
  ) => {
    let fieldVal = field;
    let element;
    let xCo = parseFloat(fieldVal?.x_pos_per);
    let yCo = parseFloat(fieldVal?.y_pos_per);
    let width = parseFloat((fieldVal?.width / 612) * 100);
    let height = parseFloat((fieldVal?.height / 792) * 100);

    // Determine the element type based on field type
    switch (fieldVal?.type) {
      case "date":
        element = FieldRenderer.createDateField(field);
        break;

      case "checkbox-list":
        element = FieldRenderer.createCheckboxField(field);
        break;

      case "radio":
        // Pass the handleFieldClick function to createRadioGroup
        return FieldRenderer.createRadioGroup(
          field,
          index,
          fieldVal,
          xCo,
          yCo,
          handleFieldClick,
          isAdmin
        );

      case "multiline":
        // Handle multiline fields with multiple positioned inputs
        return FieldRenderer.createMultilineField(
          field,
          index,
          fieldVal,
          handleFieldClick,
          isAdmin
        );

      case "string":
        element = FieldRenderer.createTextField(field);
        break;

      case "number":
        element = FieldRenderer.createNumberField(field);
        break;

      case "clause":
        element = FieldRenderer.createTextAreaField(field);
        break;

      case "people":
        element = FieldRenderer.createPeopleField(field);
        break;

      case "location":
        element = FieldRenderer.createLocationField(field);
        break;

      case "telephone":
        element = FieldRenderer.createTelephoneField(field);
        break;

      case "signature":
        element = FieldRenderer.createSignatureField(field);
        break;

      case "logo":
        element = FieldRenderer.createLogoField(field);
        break;

      default:
        element = FieldRenderer.createTextField(field);
        break;
    }

    // Only continue with common element properties if we didn't return early (for radio or multiline type)
    if (element) {
      // Set common attributes
      element.name = fieldVal?.name;
      element.title = fieldVal?.instructions;
      element.id = field?.id;

      // Position the main element absolutely
      element.style.position = "absolute";
      element.style.left = xCo + "%";
      element.style.bottom = yCo + "%";
      element.style.fontSize = 1 + "vw";
      element.style.backgroundColor = "#ccd7fd";
      element.style.border = "0";

      // Set width and height for applicable elements
      if (fieldVal?.type !== "checkbox-list" && fieldVal?.type !== "radio") {
        element.style.width = width + "%";
        element.style.height = height + "%";
      } else {
        // For checkbox-list types, set container dimensions
        element.style.width = width + "%";
        element.style.height = "auto";
        element.style.maxHeight = height + "%";
        element.style.overflow = "auto";
        element.style.padding = "8px";
        element.style.boxSizing = "border-box";
      }

      // Additional styling for specific types
      if (fieldVal?.type === "clause") {
        element.style.resize = "none";
        element.style.overflow = "auto";
      }

      element.addEventListener("click", (e) => {
        e.stopPropagation();
        if (isAdmin) {
          handleFieldClick(field);
        }
      });

      // Append the element to the field wrapper
      document.getElementById(`fieldWrap${index}`)?.appendChild(element);

      // Additional setup for signature field if needed
      if (fieldVal?.type === "signature" && !field?.value) {
        // You might want to initialize a signature pad library here
        // This is just a placeholder for where you would add that code
      }
    }
  },

  createDateField: (field) => {
    const element = document.createElement("input");
    element.type = "date";
    element.value = field?.default_value || "";
    return element;
  },

  createCheckboxField: (field) => {
    const element = document.createElement("input");
    element.type = "checkbox";
    element.checked = field?.value === true || field?.checked === true || false;
    element.style.transform = "scale(1.3)";
    return element;
  },

  createRadioGroup: (
    field,
    index,
    fieldVal,
    xCo,
    yCo,
    handleFieldClick,
    isAdmin
  ) => {
    // For radio type, we handle it differently
    // Add radio options from the options array
    if (fieldVal?.options && Array.isArray(fieldVal.options)) {
      const radioGroupName = `radio_group_${field?.id}`;
      const fieldWrapper = document.getElementById(`fieldWrap${index}`);

      // Create a parent container for the entire radio group
      // This will help us attach a single click handler to the entire group
      const radioGroupContainer = document.createElement("div");
      radioGroupContainer.id = `radio_group_container_${field?.id}`;
      radioGroupContainer.className = "radio-group-container";
      radioGroupContainer.style.position = "absolute";
      radioGroupContainer.style.left = "0";
      radioGroupContainer.style.bottom = "0";
      radioGroupContainer.style.width = "100%";
      radioGroupContainer.style.height = "100%";
      radioGroupContainer.style.pointerEvents = "none"; // Let clicks pass through to children

      // Add click handler to the entire radio group
      if (isAdmin) {
        radioGroupContainer.addEventListener("click", (e) => {
          // Only trigger if clicking on the container itself, not bubbled from children
          if (e.target === radioGroupContainer) {
            e.stopPropagation();
            handleFieldClick(field);
          }
        });
      }

      fieldWrapper?.appendChild(radioGroupContainer);

      // First, add the group label if exists (positioned at the top of all radio options)
      if (fieldVal?.label) {
        const headingElement = document.createElement("div");
        headingElement.textContent = fieldVal.label;
        headingElement.style.fontWeight = "bold";
        headingElement.style.fontSize = "1.1vw";
        headingElement.style.position = "absolute";

        // If there's at least one option with positioning, use it for the header
        if (fieldVal.options[0]?.x_pos_per && fieldVal.options[0]?.y_pos_per) {
          const xCoHeader = parseFloat(fieldVal.options[0].x_pos_per);
          // Position the header slightly above the first option
          const yCoHeader = parseFloat(fieldVal.options[0].y_pos_per) + 5;
          headingElement.style.left = xCoHeader + "%";
          headingElement.style.bottom = yCoHeader + "%";
        } else {
          // Fallback if no positioning in options
          headingElement.style.left = xCo + "%";
          headingElement.style.bottom = yCo + 5 + "%";
        }

        headingElement.style.backgroundColor = "#ccd7fd";
        headingElement.style.padding = "2px 8px";
        headingElement.style.borderRadius = "4px";
        headingElement.style.pointerEvents = "auto"; // Allow clicks on this element

        // Add click handler to heading element
        if (isAdmin) {
          headingElement.addEventListener("click", (e) => {
            e.stopPropagation();
            handleFieldClick(field);
          });
        }

        radioGroupContainer.appendChild(headingElement);
      }

      // Then add each radio option with its own positioning
      fieldVal.options.forEach((option, optionIndex) => {
        const radioContainer = document.createElement("div");
        radioContainer.style.display = "flex";
        radioContainer.style.alignItems = "center";

        // Get position for this specific option
        const xCoOption = parseFloat(option.x_pos_per || fieldVal?.x_pos_per);
        const yCoOption = parseFloat(option.y_pos_per || fieldVal?.y_pos_per);

        // Position the option container absolutely
        radioContainer.style.position = "absolute";
        radioContainer.style.left = xCoOption + "%";
        radioContainer.style.bottom = yCoOption + "%";
        radioContainer.style.backgroundColor = "#ccd7fd";
        // radioContainer.style.padding = "3px 5px";
        radioContainer.style.borderRadius = "4px";
        radioContainer.style.pointerEvents = "auto"; // Allow clicks on this element
        radioContainer.title = option.caption;

        const radioInput = document.createElement("input");
        radioInput.type = "radio";
        radioInput.name = radioGroupName;
        radioInput.id = `${field?.id}_option_${optionIndex}`;
        radioInput.fieldId = `${field?.id}`;
        radioInput.value = option.value || option;
        // radioInput.style.marginRight = "5px";
        radioInput.style.transform = "scale(1.2)";
        radioInput.title = option.caption;

        // Add the option caption label
        const radioLabel = document.createElement("label");
        radioLabel.htmlFor = `${field?.id}_option_${optionIndex}`;
        // radioLabel.textContent = option.caption;
        radioLabel.style.fontSize = "0.9vw";
        // radioLabel.style.marginLeft = "4px";
        radioLabel.title = option.caption;

        // Check if this option is selected
        if (field?.value === option.value) {
          radioInput.checked = true;
        }

        // Add click handler to radio container
        if (isAdmin) {
          radioContainer.addEventListener("click", (e) => {
            e.stopPropagation();
            handleFieldClick(field);
          });

          // Make sure clicks on input and label also trigger the field click
          radioInput.addEventListener("click", (e) => {
            if (isAdmin) {
              e.stopPropagation();
              handleFieldClick(field);
            }
          });

          radioLabel.addEventListener("click", (e) => {
            if (isAdmin) {
              e.stopPropagation();
              handleFieldClick(field);
            }
          });
        }

        radioContainer.appendChild(radioInput);
        radioContainer.appendChild(radioLabel);
        radioGroupContainer.appendChild(radioContainer);
      });

      // Return null since we've directly appended elements to the wrapper
      return null;
    } else {
      // Fallback for when options aren't provided
      const element = document.createElement("input");
      element.type = "radio";
      element.checked = field?.checked || false;
      element.style.transform = "scale(1.2)";
      return element;
    }
  },

  createMultilineField: (field, index, fieldVal, handleFieldClick, isAdmin) => {
    const fieldWrapper = document.getElementById(`fieldWrap${index}`);

    // Check if fields_collection exists and has items
    if (
      !fieldVal?.fields_collection ||
      !Array.isArray(fieldVal.fields_collection)
    ) {
      // Fallback: create a single textarea if no fields_collection
      const element = document.createElement("textarea");
      element.value = fieldVal?.default_value || "";
      return element;
    }

    // Create a parent container for the entire multiline field group
    const multilineContainer = document.createElement("div");
    multilineContainer.id = `multiline_container_${field?.id}`;
    multilineContainer.className = "multiline-container";
    multilineContainer.style.position = "absolute";
    multilineContainer.style.left = "0";
    multilineContainer.style.bottom = "0";
    multilineContainer.style.width = "100%";
    multilineContainer.style.height = "100%";
    multilineContainer.style.pointerEvents = "none"; // Let clicks pass through to children

    fieldWrapper?.appendChild(multilineContainer);

    // Sort fields_collection by index to ensure proper order
    const sortedFields = [...fieldVal.fields_collection].sort(
      (a, b) => a.index - b.index
    );

    // Split the default_value into lines for distribution across fields
    const defaultText = fieldVal?.default_value || "";
    const textLines = FieldRenderer.distributeTextAcrossFields(
      defaultText,
      sortedFields
    );

    // Create individual input fields for each item in fields_collection
    sortedFields.forEach((fieldItem, itemIndex) => {
      const inputElement = document.createElement("input");
      inputElement.type = "text";
      inputElement.id = `${field?.id}_line_${fieldItem.index}`;
      inputElement.name = `${fieldVal?.name}_${fieldItem.index}`;
      inputElement.className = "multiline-input";

      // Set the distributed text for this line
      inputElement.value = textLines[itemIndex] || "";

      // Set max characters if specified
      if (fieldItem.max_characters) {
        inputElement.maxLength = fieldItem.max_characters;
      }

      // Calculate position and dimensions
      const xCoInput = parseFloat(fieldItem.x_pos_per);
      const yCoInput = parseFloat(fieldItem.y_pos_per);
      const widthInput = parseFloat((fieldItem.width / 612) * 100);
      const heightInput = parseFloat((fieldItem.height / 792) * 100);

      // Style the input element
      inputElement.style.position = "absolute";
      inputElement.style.left = xCoInput + "%";
      inputElement.style.bottom = yCoInput + "%";
      inputElement.style.width = widthInput + "%";
      inputElement.style.height = heightInput + "%";
      inputElement.style.fontSize = "1vw";
      inputElement.style.backgroundColor = "#ccd7fd";
      inputElement.style.border = "1px solid #999";
      inputElement.style.borderRadius = "2px";
      inputElement.style.padding = "2px 4px";
      inputElement.style.boxSizing = "border-box";
      inputElement.style.pointerEvents = "auto"; // Allow interaction
      inputElement.title = fieldVal?.instructions || `Line ${fieldItem.index}`;

      // Add event listeners
      if (isAdmin) {
        inputElement.addEventListener("click", (e) => {
          e.stopPropagation();
          handleFieldClick(field);
        });
      }

      // Add input validation and auto-advance to next field functionality
      inputElement.addEventListener("input", (e) => {
        // Auto-advance to next field when max characters reached
        if (
          fieldItem.max_characters &&
          e.target.value.length >= fieldItem.max_characters
        ) {
          const nextIndex = itemIndex + 1;
          if (nextIndex < sortedFields.length) {
            const nextField = document.getElementById(
              `${field?.id}_line_${sortedFields[nextIndex].index}`
            );
            if (nextField) {
              nextField.focus();
            }
          }
        }
      });

      // Handle backspace to previous field when at beginning
      inputElement.addEventListener("keydown", (e) => {
        if (
          e.key === "Backspace" &&
          e.target.selectionStart === 0 &&
          e.target.value === ""
        ) {
          const prevIndex = itemIndex - 1;
          if (prevIndex >= 0) {
            const prevField = document.getElementById(
              `${field?.id}_line_${sortedFields[prevIndex].index}`
            );
            if (prevField) {
              prevField.focus();
              prevField.setSelectionRange(
                prevField.value.length,
                prevField.value.length
              );
            }
          }
        }
      });

      multilineContainer.appendChild(inputElement);
    });

    // Return null since we've directly appended elements to the wrapper
    return null;
  },

  // Helper function to distribute text across multiple input fields
  distributeTextAcrossFields: (text, fieldsCollection) => {
    if (!text || !fieldsCollection || fieldsCollection.length === 0) {
      return [];
    }

    const lines = [];
    let remainingText = text;

    fieldsCollection.forEach((field, index) => {
      const maxChars = field.max_characters || 110; // Default to 110 if not specified

      if (remainingText.length === 0) {
        lines.push("");
        return;
      }

      if (remainingText.length <= maxChars) {
        // Remaining text fits in this field
        lines.push(remainingText);
        remainingText = "";
      } else {
        // Need to split text - try to break at word boundaries
        let cutPoint = maxChars;

        // Look for a space to break at (going backwards from maxChars)
        while (cutPoint > 0 && remainingText[cutPoint] !== " ") {
          cutPoint--;
        }

        // If no space found, just cut at maxChars
        if (cutPoint === 0) {
          cutPoint = maxChars;
        }

        lines.push(remainingText.substring(0, cutPoint).trim());
        remainingText = remainingText.substring(cutPoint).trim();
      }
    });

    return lines;
  },

  createTextField: (field) => {
    const element = document.createElement("input");
    element.type = "text";
    element.value = field?.default_value || "";
    return element;
  },

  createNumberField: (field) => {
    const element = document.createElement("input");
    element.type = "number";
    element.value = field?.default_value || "";
    return element;
  },

  createTextAreaField: (field) => {
    const element = document.createElement("textarea");
    element.value = field?.default_value || "";
    return element;
  },

  createPeopleField: (field) => {
    const element = document.createElement("input");
    element.type = "text";
    element.placeholder = "Enter name";
    element.value = field?.default_value || "";
    return element;
  },

  createLocationField: (field) => {
    const element = document.createElement("input");
    element.type = "text";
    element.placeholder = "Enter location";
    element.value = field?.default_value || "";
    return element;
  },

  createTelephoneField: (field) => {
    const element = document.createElement("input");
    element.type = "tel";
    element.placeholder = "Enter phone number";
    element.value = field?.default_value || "";
    return element;
  },

  createSignatureField: (field) => {
    const element = document.createElement("div");
    element.innerHTML = field?.value
      ? `<img src="${field.value}" alt="Signature" style="max-width: 100%; max-height: 100%;">`
      : '<div style="display: flex; align-items: center; justify-content: center; height: 100%; border: 1px dashed #999;">Sign here</div>';
    return element;
  },

  createLogoField: (field) => {
    const element = document.createElement("div");
    element.innerHTML =
      '<div style="display: flex; align-items: center; justify-content: center; height: 100%; border: 1px dashed #999;">Logo here</div>';
    return element;
  },
};

export default FieldRenderer;
